import { useFormContext, useWatch } from 'react-hook-form';
import { LabelType } from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { mapFormFieldsToTimesheetLabels } from 'modules/notes/common/utils';
import { TimesheetLabelFields } from 'modules/notes/common/noteSchema';
import { useLabelsFormFieldContext } from 'modules/timesheet-labels';
import { DependantLabels } from '../components/schema';

export const getLabelForType = (
  queryType: LabelType,
  fields: TimesheetLabelFields
): {
  label?: TimesheetLabel | null;
  index: number;
} => {
  const labels = mapFormFieldsToTimesheetLabels(fields);
  const index = labels?.findIndex(
    (label: TimesheetLabel) => label.type === queryType
  );
  return {
    label: labels[index],
    index: index
  };
};

export const useDependantLabels = ({
  fieldName,
  label,
  label: { type }
}: {
  fieldName: string;
  label: TimesheetLabel;
}): DependantLabels => {
  const { control } = useFormContext();
  const {
    timesheetLabels: timesheetLabelsFormField
  } = useLabelsFormFieldContext();
  const fields = useWatch({ name: timesheetLabelsFormField, control });
  const dirtyFields = useWatch({ name: fieldName, control });

  const { label: clientLabel, index: clientLabelIndex } = getLabelForType(
    LabelType.Client,
    fields
  );

  const { label: programLabel, index: programLabelIndex } = getLabelForType(
    LabelType.Program,
    fields
  );
  const { label: projectLabel, index: projectLabelIndex } = getLabelForType(
    LabelType.Project,
    fields
  );
  const { label: taskLabel, index: taskLabelIndex } = getLabelForType(
    LabelType.Task,
    fields
  );

  const contextualLabel = dirtyFields || label;

  const { label: billingLabel, index: billingLabelIndex } = getLabelForType(
    LabelType.Billing,
    fields
  );

  return {
    ...(clientLabelIndex >= 0 && {
      client: {
        index: clientLabelIndex,
        label: type === LabelType.Client ? contextualLabel : clientLabel
      }
    }),
    ...(programLabelIndex >= 0 && {
      program: {
        index: programLabelIndex,
        label: type === LabelType.Program ? contextualLabel : programLabel
      }
    }),
    project: {
      index: projectLabelIndex,
      label: type === LabelType.Project ? contextualLabel : projectLabel
    },
    task: {
      index: taskLabelIndex,
      label: type === LabelType.Task ? contextualLabel : taskLabel
    },
    billing: {
      index: billingLabelIndex,
      label: type === LabelType.Billing ? contextualLabel : billingLabel
    }
  };
};
